import AdminTable from '../../../components/admin/AdminTable';
// src/pages/Admin/correspondencia/CorrespondenciasPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
    Mail, Search, RefreshCw, Eye, Pencil, Shield, 
    QrCode, KeyRound, CheckCircle2, Image as ImageIcon, X, AlertCircle, Plus, Camera, MessageSquare,
    Copy, Check, Send, ExternalLink, Bell, UserCheck, Building2, Package
} from 'lucide-react';
import { 
    fetchCorrespondencias, 
    Correspondencia, 
    validarQrCorrespondencia 
} from '../../../services/correspondencia';
import EditarCorrespondenciaModal from './EditarCorrespondenciaModal';
import NuevaCorrespondenciaModal from './NuevaCorrespondenciaModal';
import ValidarEntregaModal from './ValidarEntregaModal';
import NotificarLlegadaModal from './NotificarLlegadaModal';
import Pagination from '../../../components/Pagination/Pagination';
import CameraQrScanner from '../../../components/common/CameraQrScanner';

const CorrespondenciasPage: React.FC = () => {
    const [correspondencias, setCorrespondencias] = useState<Correspondencia[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState<string>('todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [correspondenciaSeleccionada, setCorrespondenciaSeleccionada] = useState<Correspondencia | null>(null);
    const [verFotoModal, setVerFotoModal] = useState<string | null>(null);
    
    // Modal de validación rápida de QR / PIN / Código
    const [mostrarModalValidarQR, setMostrarModalValidarQR] = useState(false);
    const [tokenOcodigo, setTokenOcodigo] = useState('');
    const [resultadoValidacion, setResultadoValidacion] = useState<Correspondencia | null>(null);
    const [errorValidacion, setErrorValidacion] = useState<string | null>(null);
    const [validando, setValidando] = useState(false);

    // Modal de entrega directa con PIN y captura de foto
    const [entregaModalTarget, setEntregaModalTarget] = useState<Correspondencia | null>(null);

    // Modal para confirmar llegada física y notificar al usuario (con foto opcional)
    const [notificarLlegadaTarget, setNotificarLlegadaTarget] = useState<Correspondencia | null>(null);

    // Modal de nueva correspondencia
    const [mostrarModalNueva, setMostrarModalNueva] = useState(false);

    // Scanner QR con cámara
    const [modoEscaner, setModoEscaner] = useState(false);

    // Modal de selección de envío por WhatsApp (Destinatario o Remitente)
    const [whatsappModalItem, setWhatsappModalItem] = useState<Correspondencia | null>(null);
    const [copiado, setCopiado] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchCorrespondencias();
            setCorrespondencias(data);
        } catch (err) {
            console.error('Error cargando correspondencias:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const formatDateTime = (dateString: string | null): string => {
        if (!dateString) return 'Pendiente';
        const date = new Date(dateString);
        return date.toLocaleString('es-BO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const getEstadoColor = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'notificado':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'entregado':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'cancelado':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getEstadoTexto = (estado: string) => {
        switch (estado) {
            case 'recibido': return 'En Recepción';
            case 'notificado': return 'Notificado';
            case 'entregado': return 'Entregado';
            case 'cancelado': return 'Cancelado';
            default: return estado;
        }
    };

    const procesarValidacion = async (termino: string) => {
        const query = termino.trim();
        if (!query) return;

        setValidando(true);
        setErrorValidacion(null);
        setResultadoValidacion(null);

        const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

        const findInList = (items: Correspondencia[]) => {
            const term = query.toLowerCase();
            const termNorm = normalize(query);
            const digitsOnly = query.replace(/[^0-9]/g, '');

            // Búsqueda exacta primero
            const exact = items.find(c =>
                (c.token_verificacion && c.token_verificacion === query) ||
                (c.pin_seguridad && c.pin_seguridad === query) ||
                (c.codigo && c.codigo.toLowerCase() === term) ||
                (c.codigo && normalize(c.codigo) === termNorm) ||
                String(c.id) === query.replace('#', '') ||
                (digitsOnly && String(c.id) === digitsOnly)
            );
            if (exact) return exact;

            // Búsqueda parcial por código (ej: "CORR-2" o "CORR-3" encuentra "CORR/26/00003")
            if (termNorm.length >= 2) {
                return items.find(c =>
                    (c.codigo && normalize(c.codigo).includes(termNorm)) ||
                    (c.codigo && c.codigo.toLowerCase().includes(term))
                );
            }
            return undefined;
        };

        // 1. Buscar en la lista en memoria o refrescar
        let found = findInList(correspondencias);
        if (!found) {
            try {
                const fresh = await fetchCorrespondencias();
                setCorrespondencias(fresh);
                found = findInList(fresh);
            } catch (e) {
                // ignore
            }
        }

        if (found) {
            setModoEscaner(false);
            setValidando(false);
            if (found.estado !== 'entregado' && found.estado !== 'cancelado') {
                setMostrarModalValidarQR(false);
                setEntregaModalTarget(found);
            } else {
                setResultadoValidacion(found);
            }
            return;
        }

        // 2. Intentar endpoint backend como fallback
        try {
            const res = await validarQrCorrespondencia(query);
            setModoEscaner(false);
            if (res && res.estado !== 'entregado' && res.estado !== 'cancelado') {
                setMostrarModalValidarQR(false);
                setEntregaModalTarget(res);
            } else {
                setResultadoValidacion(res);
            }
        } catch (err: any) {
            setErrorValidacion(`No se encontró correspondencia para el código, QR o PIN "${query}".`);
        } finally {
            setValidando(false);
        }
    };

    const handleValidarQR = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        await procesarValidacion(tokenOcodigo);
    };

    const handleQrScan = (decodedText: string) => {
        setModoEscaner(false);
        setTokenOcodigo(decodedText);
        procesarValidacion(decodedText);
    };

    const enviarWhatsAppDestinatario = (item: Correspondencia) => {
        let telefono = item.destinatario_telefono || '';
        const nombreDest = item.destinatario_nombre || item.cliente_nombre || 'Estimado(a)';
        const digits = telefono.replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;

        const trackingUrl = `${window.location.origin}/seguimiento/${encodeURIComponent(item.codigo || String(item.id))}`;
        const remitenteFull = `${item.remitente_nombre} ${item.remitente_apellido || ''}`.trim();

        const mensaje = `Hola *${nombreDest}*, te informamos que tienes un paquete listo para recoger en recepción de *Comarca Coworking*, dejado por *${remitenteFull}*.\n\n` +
            `📦 *Código de Paquete:* ${item.codigo || `#${item.id}`}\n` +
            `🔐 *PIN de Retiro:* ${item.pin_seguridad}\n\n` +
            `📱 *Puedes ver tu Comprobante y Código QR de retiro aquí:*\n${trackingUrl}\n\n` +
            `_Presenta tu Código QR o dicta tu PIN en recepción al momento de recoger._`;

        window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`, '_blank');
    };

    const enviarWhatsAppRemitente = (item: Correspondencia) => {
        let telefono = item.remitente_telefono || '';
        const remitenteFull = `${item.remitente_nombre} ${item.remitente_apellido || ''}`.trim() || 'Estimado(a)';
        const nombreDest = item.destinatario_nombre || item.cliente_nombre || 'el destinatario';
        const digits = telefono.replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;

        const trackingUrl = `${window.location.origin}/seguimiento/${encodeURIComponent(item.codigo || String(item.id))}`;

        const mensaje = `Hola *${remitenteFull}*, confirmamos que tenemos registrado en custodia tu paquete en *Comarca Coworking* para *${nombreDest}*.\n\n` +
            `📦 *Código de Paquete:* ${item.codigo || `#${item.id}`}\n` +
            `🔐 *PIN de Retiro:* ${item.pin_seguridad}\n\n` +
            `📱 *Comprobante y Seguimiento en vivo:*\n${trackingUrl}\n\n` +
            `_Te notificaremos cuando el paquete haya sido retirado._`;

        window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`, '_blank');
    };

    const copiarEnlace = (item: Correspondencia) => {
        const trackingUrl = `${window.location.origin}/seguimiento/${encodeURIComponent(item.codigo || String(item.id))}`;
        navigator.clipboard.writeText(trackingUrl);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2500);
    };

    // Filtrado
    const correspondenciasFiltradas = correspondencias.filter(item => {
        if (filtroEstado !== 'todos' && item.estado !== filtroEstado) {
            return false;
        }

        if (busqueda.trim()) {
            const term = busqueda.toLowerCase();
            return (
                (item.codigo && item.codigo.toLowerCase().includes(term)) ||
                (item.cliente_nombre && item.cliente_nombre.toLowerCase().includes(term)) ||
                (item.destinatario_nombre && item.destinatario_nombre.toLowerCase().includes(term)) ||
                (item.descripcion && item.descripcion.toLowerCase().includes(term)) ||
                item.remitente_nombre.toLowerCase().includes(term) ||
                item.remitente_apellido.toLowerCase().includes(term) ||
                item.remitente_ci_nit.toLowerCase().includes(term) ||
                (item.receptor_nombre && item.receptor_nombre.toLowerCase().includes(term)) ||
                (item.pin_seguridad && item.pin_seguridad.includes(term))
            );
        }

        return true;
    });

    const totalPages = Math.ceil(correspondenciasFiltradas.length / itemsPerPage);
    const paginatedItems = correspondenciasFiltradas.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Contadores
    const totalRecibidas = correspondencias.filter(c => c.estado === 'recibido').length;
    const totalNotificadas = correspondencias.filter(c => c.estado === 'notificado').length;
    const totalEntregadas = correspondencias.filter(c => c.estado === 'entregado').length;

    return (
        <div data-admin-page="true" className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
            {/* Modal de foto en pantalla completa */}
            {verFotoModal && (
                <div 
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[80] p-4 cursor-pointer backdrop-blur-sm"
                    onClick={() => setVerFotoModal(null)}
                >
                    <div className="relative max-w-3xl max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-2" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setVerFotoModal(null)}
                            className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <img src={verFotoModal} alt="Foto Ampliada" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                    </div>
                </div>
            )}

            {/* Modal de Validación Rápida de QR / PIN */}
            {mostrarModalValidarQR && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto custom-scroll">
                        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-blue-100 text-[#61257b] rounded-xl">
                                    <QrCode className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">Escanear / Validar Entrega</h3>
                                    <p className="text-xs text-gray-500">Usa la cámara o ingresa el código manualmente</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setModoEscaner(false);
                                    setMostrarModalValidarQR(false);
                                    setResultadoValidacion(null);
                                    setErrorValidacion(null);
                                    setTokenOcodigo('');
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Selector de modo: Manual vs Cámara */}
                        <div className="flex gap-2 p-1 bg-gray-100 rounded-2xl">
                            <button
                                type="button"
                                onClick={() => setModoEscaner(false)}
                                className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${
                                    !modoEscaner
                                        ? 'bg-white text-[#61257b] shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <KeyRound className="w-4 h-4" />
                                Código / PIN
                            </button>
                            <button
                                type="button"
                                onClick={() => setModoEscaner(true)}
                                className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${
                                    modoEscaner
                                        ? 'bg-[#61257b] text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <Camera className="w-4 h-4" />
                                Escanear Cámara QR
                            </button>
                        </div>

                        {/* Modo 1: Escáner QR con Cámara (React Nativo) */}
                        {modoEscaner && (
                            <div className="space-y-3">
                                <CameraQrScanner
                                    isActive={modoEscaner && mostrarModalValidarQR}
                                    onScan={handleQrScan}
                                    onError={(err) => setErrorValidacion(err)}
                                />
                                <p className="text-center text-xs text-gray-500">
                                    Apunta la cámara al código QR del comprobante de correspondencia
                                </p>
                            </div>
                        )}

                        {/* Entrada manual */}
                        {!modoEscaner && (
                            <form onSubmit={handleValidarQR} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                                        Código QR, Código de Paquete o PIN
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={tokenOcodigo}
                                            onChange={(e) => setTokenOcodigo(e.target.value)}
                                            placeholder="Ej: CORR/26/00001, PIN de 6 dígitos o Token QR"
                                            className="w-full pl-4 pr-12 py-3 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#61257b] focus:border-[#61257b] outline-none"
                                            autoFocus
                                        />
                                        <button
                                            type="submit"
                                            disabled={validando || !tokenOcodigo.trim()}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1.5 bg-[#61257b] text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                                        >
                                            {validando ? 'Buscando...' : 'Buscar'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}

                        {errorValidacion && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-xs">
                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                <span>{errorValidacion}</span>
                            </div>
                        )}

                        {resultadoValidacion && (
                            <div className="p-4 bg-blue-50/70 border border-blue-200 rounded-2xl space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="font-mono font-bold text-[#61257b] text-base">
                                        {resultadoValidacion.codigo || `#${resultadoValidacion.id}`}
                                    </span>
                                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getEstadoColor(resultadoValidacion.estado)}`}>
                                        {getEstadoTexto(resultadoValidacion.estado)}
                                    </span>
                                </div>

                                <p className="text-xs text-gray-700">{resultadoValidacion.descripcion || 'Sin descripción'}</p>

                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 pt-2 border-t border-blue-100">
                                    <div>
                                        <span className="text-gray-400 block">Remitente:</span>
                                        <p className="font-medium">{resultadoValidacion.remitente_nombre} {resultadoValidacion.remitente_apellido}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">PIN de Retiro:</span>
                                        <p className="font-mono font-bold text-[#61257b]">{resultadoValidacion.pin_seguridad || 'N/A'}</p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setModoEscaner(false);
                                        setMostrarModalValidarQR(false);
                                        if (resultadoValidacion.estado !== 'entregado' && resultadoValidacion.estado !== 'cancelado') {
                                            setEntregaModalTarget(resultadoValidacion);
                                        } else {
                                            setCorrespondenciaSeleccionada(resultadoValidacion);
                                        }
                                    }}
                                    className="w-full py-2.5 px-4 text-xs font-semibold text-white bg-gradient-to-r from-[#61257b] to-[#8b3fa8] hover:shadow-md rounded-xl transition-all flex items-center justify-center gap-2"
                                >
                                    <Shield className="w-4 h-4" />
                                    {resultadoValidacion.estado === 'entregado' ? 'Ver Detalles' : 'Validar y Entregar (PIN)'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cabecera Principal */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#61257b] to-[#8b3fa8] flex items-center justify-center text-white shadow-md">
                        <Mail className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 admin-page-title">Gestión de <span className="admin-title-accent">Correspondencia</span></h1>
                        <p className="text-sm text-gray-500">Administra paquetes, códigos alfanuméricos y validación de entregas</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setMostrarModalNueva(true)}
                        className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Nueva Correspondencia
                    </button>
                    <button
                        onClick={() => setMostrarModalValidarQR(true)}
                        className="px-4 py-2.5 bg-gradient-to-r from-[#61257b] to-[#8b3fa8] text-white text-sm font-semibold rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                    >
                        <QrCode className="w-4 h-4" />
                        Validar QR / PIN
                    </button>
                    <button
                        onClick={loadData}
                        className="p-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors"
                        title="Recargar datos"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Tarjetas de Estadísticas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">En Recepción</span>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{totalRecibidas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Mail className="w-5 h-5" />
                    </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <span className="text-xs font-medium text-amber-600 uppercase tracking-wider">Listo para Retiro</span>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{totalNotificadas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                        <KeyRound className="w-5 h-5" />
                    </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <span className="text-xs font-medium text-green-600 uppercase tracking-wider">Entregados</span>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{totalEntregadas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5" />
                    </div>
                </div>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por código (ej: CORR/26/00001), remitente, receptor, PIN o descripción..."
                        value={busqueda}
                        onChange={(e) => {
                            setBusqueda(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] w-full text-sm"
                    />
                </div>

                <div className="flex gap-2">
                    <select
                        value={filtroEstado}
                        onChange={(e) => {
                            setFiltroEstado(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] text-sm"
                    >
                        <option value="todos">Todos los Estados</option>
                        <option value="recibido">En Recepción</option>
                        <option value="notificado">Notificado</option>
                        <option value="entregado">Entregado</option>
                        <option value="cancelado">Cancelado</option>
                    </select>

                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] text-sm text-gray-600 bg-white"
                        title="Registros por página"
                    >
                        <option value={10}>10 por pág.</option>
                        <option value={20}>20 por pág.</option>
                        <option value={50}>50 por pág.</option>
                    </select>
                </div>
            </div>

            {/* Tabla de Correspondencias */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="text-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#61257b] mx-auto"></div>
                        <p className="mt-4 text-gray-500 text-sm">Cargando correspondencias...</p>
                    </div>
                ) : paginatedItems.length > 0 ? (
                    <div className="overflow-x-auto">
                        <AdminTable className="w-full text-left text-sm text-gray-600">
                            <thead className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4">Foto</th>
                                    <th className="px-6 py-4">Código / Cita</th>
                                    <th className="px-6 py-4">Descripción</th>
                                    <th className="px-6 py-4">Remitente</th>
                                    <th className="px-6 py-4">PIN / Receptor</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4">Fecha Recepción / Recepcionista</th>
                                    <th className="px-6 py-4 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {paginatedItems.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                                        {/* Foto(s) */}
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1.5">
                                                {item.imagen && (
                                                    <div 
                                                        className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 cursor-pointer border border-gray-200 relative group"
                                                        onClick={() => setVerFotoModal(item.imagen || null)}
                                                        title="Foto de Recepción (Custodia)"
                                                    >
                                                        <img src={item.imagen} alt="Paquete" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[9px]">
                                                            <Eye className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                )}
                                                {item.imagen_entrega && (
                                                    <div 
                                                        className="w-10 h-10 rounded-xl overflow-hidden bg-emerald-50 cursor-pointer border border-emerald-300 relative group"
                                                        onClick={() => setVerFotoModal(item.imagen_entrega || null)}
                                                        title="Foto de Entrega (Respaldo)"
                                                    >
                                                        <img src={item.imagen_entrega} alt="Entrega" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[9px]">
                                                            <Eye className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                )}
                                                {!item.imagen && !item.imagen_entrega && (
                                                    <div className="w-10 h-10 rounded-xl bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                                                        <ImageIcon className="w-4 h-4 opacity-50" />
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Código y Destinatario */}
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                                <span className="font-mono font-bold text-[#61257b] bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200 text-xs">
                                                    {item.codigo || `#${item.id}`}
                                                </span>
                                                {item.afiliacion_empresa && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-bold rounded-md border border-purple-200">
                                                        <Building2 className="w-3 h-3" /> {item.afiliacion_empresa}
                                                    </span>
                                                )}
                                                {item.servicio_nombre && !item.afiliacion_empresa && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-md border border-blue-200">
                                                        <Package className="w-3 h-3" /> {item.servicio_nombre}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-gray-700 font-medium">
                                                Para: <span className="font-bold text-gray-900">{item.cliente_nombre || item.destinatario_nombre || 'Cliente'}</span>
                                            </p>
                                            {item.destinatario_telefono && (
                                                <p className="text-[10px] text-gray-400">Tel: {item.destinatario_telefono}</p>
                                            )}
                                        </td>

                                        {/* Descripción */}
                                        <td className="px-6 py-4 max-w-xs truncate">
                                            <p className="font-medium text-gray-800 truncate" title={item.descripcion || ''}>
                                                {item.descripcion || 'Sin descripción'}
                                            </p>
                                        </td>

                                        {/* Remitente */}
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-gray-800">{item.remitente_nombre} {item.remitente_apellido}</p>
                                            <p className="text-[11px] text-gray-500">{item.remitente_ci_nit}</p>
                                        </td>

                                        {/* PIN / Receptor */}
                                        <td className="px-6 py-4">
                                            {item.estado === 'entregado' ? (
                                                <div>
                                                    <p className="font-semibold text-green-700">{item.receptor_nombre || 'Cliente'}</p>
                                                    <p className="text-[11px] text-gray-500">{item.receptor_ci_nit}</p>
                                                </div>
                                            ) : (
                                                item.pin_seguridad && (
                                                    <span className="inline-flex items-center gap-1 font-mono font-bold text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded border">
                                                        <Shield className="w-3 h-3 text-[#61257b]" />
                                                        PIN: {item.pin_seguridad}
                                                    </span>
                                                )
                                            )}
                                        </td>

                                        {/* Estado */}
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getEstadoColor(item.estado)}`}>
                                                {getEstadoTexto(item.estado)}
                                            </span>
                                        </td>

                                        {/* Fecha y Recepcionista */}
                                        <td className="px-6 py-4 text-xs">
                                            <p className="font-semibold text-gray-800">{formatDateTime(item.fecha_recepcion)}</p>
                                            <p className="text-[11px] text-gray-600 flex items-center gap-1 mt-1" title="Personal de recepción">
                                                <UserCheck className="w-3.5 h-3.5 text-[#61257b]" />
                                                <span>Recibió: <strong className="text-gray-900">{item.usuario_recibe_nombre || 'Recepción Comarca'}</strong></span>
                                            </p>
                                            {item.estado === 'entregado' && item.usuario_entrega_nombre && (
                                                <p className="text-[10px] text-emerald-700 flex items-center gap-1 mt-0.5" title="Personal que entregó">
                                                    <span>Entregó: <strong>{item.usuario_entrega_nombre}</strong></span>
                                                </p>
                                            )}
                                        </td>

                                        {/* Acciones */}
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {item.estado !== 'entregado' && item.estado !== 'cancelado' && (
                                                    <>
                                                        <button
                                                            onClick={() => setNotificarLlegadaTarget(item)}
                                                            className="px-2.5 py-1.5 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors inline-flex items-center gap-1 shadow-sm"
                                                            title="Confirmar llegada física y notificar al cliente"
                                                        >
                                                            <Bell className="w-3.5 h-3.5 text-amber-600" />
                                                            Notificar Llegada
                                                        </button>
                                                        <button
                                                            onClick={() => setWhatsappModalItem(item)}
                                                            className="p-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors inline-flex items-center"
                                                            title="Enviar Notificación por WhatsApp"
                                                        >
                                                            <MessageSquare className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEntregaModalTarget(item)}
                                                            className="px-2.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors inline-flex items-center gap-1 shadow-sm"
                                                            title="Entregar con PIN"
                                                        >
                                                            <Shield className="w-3.5 h-3.5" />
                                                            Entregar
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => setCorrespondenciaSeleccionada(item)}
                                                    className="px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors inline-flex items-center gap-1"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                    Gestionar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </AdminTable>
                    </div>
                ) : (
                    <div className="text-center py-20">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Mail className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-800 mb-1">
                            No se encontraron correspondencias
                        </h3>
                        <p className="text-gray-500 text-sm max-w-md mx-auto">
                            {busqueda || filtroEstado !== 'todos'
                                ? 'Prueba con otros términos de búsqueda o filtros.'
                                : 'No hay paquetes registrados en el sistema.'}
                        </p>
                    </div>
                )}

                {/* Paginación */}
                {correspondenciasFiltradas.length > 0 && (
                    <div className="p-4 border-t border-gray-100">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => setCurrentPage(page)}
                            itemsPerPage={itemsPerPage}
                            totalItems={correspondenciasFiltradas.length}
                        />
                    </div>
                )}
            </div>

            {/* Modal de edición / entrega */}
            {correspondenciaSeleccionada && (
                <EditarCorrespondenciaModal
                    isOpen={!!correspondenciaSeleccionada}
                    onClose={() => setCorrespondenciaSeleccionada(null)}
                    correspondencia={correspondenciaSeleccionada}
                    onSave={async () => {
                        await loadData();
                        setCorrespondenciaSeleccionada(null);
                    }}
                />
            )}

            {/* Modal de nueva correspondencia */}
            <NuevaCorrespondenciaModal
                isOpen={mostrarModalNueva}
                onClose={() => setMostrarModalNueva(false)}
                onSave={async () => {
                    await loadData();
                }}
            />

            {/* Modal directo de Validación y Entrega con PIN y Foto */}
            {entregaModalTarget && (
                <ValidarEntregaModal
                    isOpen={Boolean(entregaModalTarget)}
                    correspondencia={entregaModalTarget}
                    onClose={() => setEntregaModalTarget(null)}
                    onSuccess={async (updated) => {
                        await loadData();
                        setEntregaModalTarget(null);
                        alert(`¡Correspondencia ${updated.codigo || `#${updated.id}`} entregada y validada con PIN exitosamente!`);
                    }}
                />
            )}

            {/* Modal para Confirmar Llegada Física y Notificar al Usuario */}
            {notificarLlegadaTarget && (
                <NotificarLlegadaModal
                    isOpen={Boolean(notificarLlegadaTarget)}
                    correspondencia={notificarLlegadaTarget}
                    onClose={() => setNotificarLlegadaTarget(null)}
                    onSuccess={async () => {
                        await loadData();
                    }}
                />
            )}

            {/* Modal de Envío por WhatsApp (Destinatario / Remitente) */}
            {whatsappModalItem && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4 backdrop-blur-sm font-['DM_Sans',sans-serif]">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-scale-up border border-gray-100">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#61257b] to-[#8b3fa8] p-6 text-white text-center relative">
                            <button
                                onClick={() => setWhatsappModalItem(null)}
                                className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-2xl mb-2 backdrop-blur-sm">
                                <MessageSquare className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-black">Notificar por WhatsApp</h3>
                            <p className="text-xs text-white/90 font-mono mt-0.5">
                                Paquete: {whatsappModalItem.codigo || `#${whatsappModalItem.id}`}
                            </p>
                        </div>

                        {/* Contenido */}
                        <div className="p-6 space-y-4">
                            {/* Resumen del paquete */}
                            <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 text-xs space-y-1.5">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 font-semibold">Remitente (Dejó):</span>
                                    <span className="font-bold text-gray-800">
                                        {whatsappModalItem.remitente_nombre} {whatsappModalItem.remitente_apellido || ''}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500 font-semibold">Destinatario (Recoge):</span>
                                    <span className="font-bold text-[#61257b]">
                                        {whatsappModalItem.destinatario_nombre || whatsappModalItem.cliente_nombre || 'Cliente'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500 font-semibold">PIN de Retiro:</span>
                                    <span className="font-mono font-black text-gray-900 bg-white px-2 py-0.5 rounded border">
                                        {whatsappModalItem.pin_seguridad}
                                    </span>
                                </div>
                            </div>

                            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider text-center">
                                Selecciona a quién enviar el mensaje:
                            </p>

                            {/* Opción 1: Destinatario */}
                            <button
                                onClick={() => {
                                    enviarWhatsAppDestinatario(whatsappModalItem);
                                    setWhatsappModalItem(null);
                                }}
                                className="w-full p-4 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-between text-left group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-white/20 rounded-xl">
                                        <MessageSquare className="w-5 h-5 fill-white" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold">Enviar al Destinatario (Quién recogerá)</div>
                                        <div className="text-xs text-white/90">
                                            {whatsappModalItem.destinatario_nombre || 'Destinatario'} 
                                            {whatsappModalItem.destinatario_telefono ? ` • Cel: ${whatsappModalItem.destinatario_telefono}` : ''}
                                        </div>
                                    </div>
                                </div>
                                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>

                            {/* Opción 2: Remitente */}
                            <button
                                onClick={() => {
                                    enviarWhatsAppRemitente(whatsappModalItem);
                                    setWhatsappModalItem(null);
                                }}
                                className="w-full p-4 bg-[#61257b] hover:bg-[#006ba8] text-white rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-between text-left group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-white/20 rounded-xl">
                                        <MessageSquare className="w-5 h-5 fill-white" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold">Enviar Comprobante al Remitente (Quién dejó)</div>
                                        <div className="text-xs text-white/90">
                                            {whatsappModalItem.remitente_nombre} 
                                            {whatsappModalItem.remitente_telefono ? ` • Cel: ${whatsappModalItem.remitente_telefono}` : ''}
                                        </div>
                                    </div>
                                </div>
                                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>

                            {/* Opción 3: Copiar Link de Seguimiento */}
                            <button
                                onClick={() => copiarEnlace(whatsappModalItem)}
                                className="w-full p-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-xl flex items-center justify-center gap-2 transition-colors"
                            >
                                {copiado ? (
                                    <>
                                        <Check className="w-4 h-4 text-emerald-600" />
                                        <span className="text-emerald-700 font-bold">¡Enlace Copiado al Portapapeles!</span>
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        <span>Copiar Enlace de Seguimiento / Comprobante</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CorrespondenciasPage;

